/**
 * One status language for Warehouse, Invoicing and Inventory records (docs/status-language.md).
 *
 * Every list chip, record-header chip and tracker step reads its label and tone from here, so
 * the same stage looks the same on transfers, purchase orders, subrentals, invoices and master
 * bills. Raw database values never reach the screen. "Late" is not a status: it is a date flag
 * (see lateness.ts), and a late record keeps its status chip.
 */

export type StatusTone = 'neutral' | 'info' | 'progress' | 'warn' | 'ok' | 'stopped';

export type StatusDocumentKind =
  | 'transfer'
  | 'transferLine'
  | 'transferSerial'
  | 'transferRequest'
  | 'purchaseOrder'
  | 'purchaseOrderLine'
  | 'subrental'
  | 'subrentalUnit'
  | 'invoice'
  | 'masterBill'
  | 'asset'
  | 'inventoryRun'
  | 'yearlyCampaign'
  | 'yearlyRequest'
  | 'kitBuild'
  | 'inventoryAlert';

export type StatusDescription = {
  /** The raw value, lower-cased and trimmed ('' when missing). */
  value: string;
  /** Sentence-case label for chips and trackers. */
  label: string;
  tone: StatusTone;
};

type StatusEntry = { label: string; tone: StatusTone };
type StatusMap = { readonly [status: string]: StatusEntry };

const TRANSFER: StatusMap = {
  draft: { label: 'Draft', tone: 'neutral' },
  confirmed: { label: 'Confirmed', tone: 'info' },
  partially_picked: { label: 'Partially picked', tone: 'progress' },
  picked: { label: 'Picked', tone: 'progress' },
  partially_shipped: { label: 'Partially shipped', tone: 'progress' },
  shipped: { label: 'Shipped', tone: 'progress' },
  in_transit: { label: 'In transit', tone: 'progress' },
  partially_received: { label: 'Partially received', tone: 'warn' },
  received: { label: 'Received', tone: 'ok' },
  complete: { label: 'Complete', tone: 'ok' },
  cancelled: { label: 'Cancelled', tone: 'stopped' },
};

const TRANSFER_LINE: StatusMap = {
  pending: { label: 'Not picked', tone: 'neutral' },
  partially_picked: { label: 'Partially picked', tone: 'progress' },
  picked: { label: 'Picked', tone: 'progress' },
  partially_shipped: { label: 'Partially shipped', tone: 'progress' },
  shipped: { label: 'Shipped', tone: 'progress' },
  in_transit: { label: 'In transit', tone: 'progress' },
  partially_received: { label: 'Partially received', tone: 'warn' },
  received: { label: 'Received', tone: 'ok' },
  kept: { label: 'Kept', tone: 'ok' },
  cancelled: { label: 'Cancelled', tone: 'stopped' },
};

/** Normalised serial states; the record reader maps R2's raw serial codes onto these. */
const TRANSFER_SERIAL: StatusMap = {
  assigned: { label: 'Assigned', tone: 'neutral' },
  picked: { label: 'Picked', tone: 'progress' },
  shipped: { label: 'Shipped', tone: 'progress' },
  in_transit: { label: 'In transit', tone: 'progress' },
  received: { label: 'Received', tone: 'ok' },
  kept: { label: 'Kept', tone: 'ok' },
  cancelled: { label: 'Cancelled', tone: 'stopped' },
};

/** Transfer requests (shortage broadcasts) on the Requests tab. */
const TRANSFER_REQUEST: StatusMap = {
  open: { label: 'Open', tone: 'info' },
  awarded: { label: 'Awarded', tone: 'progress' },
  accepted: { label: 'Accepted', tone: 'progress' },
  fulfilled: { label: 'Fulfilled', tone: 'ok' },
  rejected: { label: 'Declined', tone: 'stopped' },
  expired: { label: 'Expired', tone: 'stopped' },
  cancelled: { label: 'Cancelled', tone: 'stopped' },
};

const PURCHASE_ORDER: StatusMap = {
  draft: { label: 'Draft', tone: 'neutral' },
  submitted: { label: 'Submitted', tone: 'info' },
  acknowledged: { label: 'Acknowledged', tone: 'info' },
  partially_received: { label: 'Partially received', tone: 'warn' },
  received: { label: 'Received', tone: 'ok' },
  closed: { label: 'Closed', tone: 'ok' },
  cancelled: { label: 'Cancelled', tone: 'stopped' },
};

const PURCHASE_ORDER_LINE: StatusMap = {
  pending: { label: 'Not ordered', tone: 'neutral' },
  ordered: { label: 'Ordered', tone: 'info' },
  partially_received: { label: 'Partially received', tone: 'warn' },
  received: { label: 'Received', tone: 'ok' },
  cancelled: { label: 'Cancelled', tone: 'stopped' },
};

/**
 * Subrental orders use a derived display status (warehouse list and record RPCs), and the raw
 * `subrentals.status` values map onto the same words: pending → Submitted, confirmed →
 * Acknowledged, active → On rent.
 */
const SUBRENTAL: StatusMap = {
  draft: { label: 'Draft', tone: 'neutral' },
  submitted: { label: 'Submitted', tone: 'info' },
  pending: { label: 'Submitted', tone: 'info' },
  acknowledged: { label: 'Acknowledged', tone: 'info' },
  confirmed: { label: 'Acknowledged', tone: 'info' },
  on_rent: { label: 'On rent', tone: 'progress' },
  active: { label: 'On rent', tone: 'progress' },
  partially_received: { label: 'Partially received', tone: 'warn' },
  return_overdue: { label: 'Return overdue', tone: 'warn' },
  received: { label: 'Received', tone: 'ok' },
  returned: { label: 'Returned', tone: 'ok' },
  closed: { label: 'Closed', tone: 'ok' },
  cancelled: { label: 'Cancelled', tone: 'stopped' },
};

const SUBRENTAL_UNIT: StatusMap = {
  expected: { label: 'Incoming', tone: 'neutral' },
  received: { label: 'At the base', tone: 'progress' },
  issued: { label: 'With event', tone: 'progress' },
  lost: { label: 'Lost', tone: 'warn' },
  returned: { label: 'Returned', tone: 'ok' },
};

const INVOICE: StatusMap = {
  draft: { label: 'Draft', tone: 'neutral' },
  review_requested: { label: 'Review requested', tone: 'warn' },
  posted: { label: 'Posted', tone: 'ok' },
  paid: { label: 'Paid', tone: 'ok' },
  void: { label: 'Void', tone: 'stopped' },
};

const MASTER_BILL: StatusMap = {
  draft: { label: 'Draft', tone: 'neutral' },
  posted: { label: 'Posted', tone: 'ok' },
  paid: { label: 'Paid', tone: 'ok' },
  void: { label: 'Void', tone: 'stopped' },
};

/**
 * Serialized assets. `assets.status` mixes Portal values with R2's capitalised ones ('In Service',
 * 'Out'); the lookup lower-cases them, so both spellings land on one label. In production only
 * available, retired, on_rent and Filled are in use; "in service" is mostly an open service ticket,
 * which the inventory readers report as `in_service`.
 */
const ASSET: StatusMap = {
  available: { label: 'Available', tone: 'ok' },
  reserved: { label: 'Reserved', tone: 'info' },
  picked: { label: 'Picked', tone: 'progress' },
  filled: { label: 'Filled', tone: 'progress' },
  shipped: { label: 'Shipped', tone: 'progress' },
  'in transfer': { label: 'In transfer', tone: 'progress' },
  on_rent: { label: 'On rent', tone: 'info' },
  rented: { label: 'On rent', tone: 'info' },
  out: { label: 'On rent', tone: 'info' },
  in_service: { label: 'In service', tone: 'warn' },
  'in service': { label: 'In service', tone: 'warn' },
  maintenance: { label: 'In service', tone: 'warn' },
  repair: { label: 'In service', tone: 'warn' },
  missing: { label: 'Missing', tone: 'warn' },
  lost: { label: 'Lost', tone: 'warn' },
  retired: { label: 'Retired', tone: 'stopped' },
  decommissioned: { label: 'Retired', tone: 'stopped' },
  sold: { label: 'Sold', tone: 'stopped' },
};

/** Inventory count runs (`inventory_runs.status`). "Overdue" is a date flag, not a status. */
const INVENTORY_RUN: StatusMap = {
  draft: { label: 'Draft', tone: 'neutral' },
  scheduled: { label: 'Upcoming', tone: 'neutral' },
  active: { label: 'Counting', tone: 'progress' },
  review: { label: 'In review', tone: 'info' },
  completed: { label: 'Closed', tone: 'ok' },
  cancelled: { label: 'Cancelled', tone: 'stopped' },
};

const YEARLY_CAMPAIGN: StatusMap = {
  draft: { label: 'Draft', tone: 'neutral' },
  active: { label: 'Active', tone: 'progress' },
  review: { label: 'In review', tone: 'info' },
  closed: { label: 'Closed', tone: 'ok' },
  archived: { label: 'Archived', tone: 'stopped' },
};

/** A site's yearly request; the labels match the yearly workspace's own. */
const YEARLY_REQUEST: StatusMap = {
  draft: { label: 'Draft', tone: 'neutral' },
  submitted: { label: 'Submitted', tone: 'info' },
  manager_review: { label: 'Manager review', tone: 'info' },
  manager_approved: { label: 'Manager approved', tone: 'progress' },
  manager_rejected: { label: 'Rejected by manager', tone: 'warn' },
  regional_review: { label: 'Regional review', tone: 'info' },
  regional_submitted: { label: 'Regional submitted', tone: 'progress' },
  admin_review: { label: 'Admin review', tone: 'info' },
  admin_approved: { label: 'Admin approved', tone: 'ok' },
  admin_rejected: { label: 'Rejected by admin', tone: 'warn' },
};

/** Whether a kit can be built at a site today (derived by the kit readers). */
const KIT_BUILD: StatusMap = {
  buildable: { label: 'Buildable', tone: 'ok' },
  short: { label: 'Short', tone: 'warn' },
  empty: { label: 'No components', tone: 'warn' },
};

/** Inventory alert kinds (derived by the alerts reader). */
const INVENTORY_ALERT: StatusMap = {
  idle: { label: 'Idle', tone: 'warn' },
  low_return: { label: 'Low return', tone: 'warn' },
  cooling: { label: 'Cooling', tone: 'warn' },
  low_stock: { label: 'Below reorder point', tone: 'warn' },
};

const STATUS_MAPS: { readonly [kind in StatusDocumentKind]: StatusMap } = {
  transfer: TRANSFER,
  transferLine: TRANSFER_LINE,
  transferSerial: TRANSFER_SERIAL,
  transferRequest: TRANSFER_REQUEST,
  purchaseOrder: PURCHASE_ORDER,
  purchaseOrderLine: PURCHASE_ORDER_LINE,
  subrental: SUBRENTAL,
  subrentalUnit: SUBRENTAL_UNIT,
  invoice: INVOICE,
  masterBill: MASTER_BILL,
  asset: ASSET,
  inventoryRun: INVENTORY_RUN,
  yearlyCampaign: YEARLY_CAMPAIGN,
  yearlyRequest: YEARLY_REQUEST,
  kitBuild: KIT_BUILD,
  inventoryAlert: INVENTORY_ALERT,
};

function normalizeStatusValue(raw: string | null | undefined): string {
  return String(raw ?? '').trim().toLowerCase();
}

/** Sentence-case fallback for a value the map does not know yet ("on_hold" → "On hold"). */
export function sentenceCaseStatus(raw: string | null | undefined): string {
  const words = normalizeStatusValue(raw).split(/[_\s-]+/).filter(Boolean);
  if (!words.length) return 'Unknown';
  const sentence = words.join(' ');
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}

export function describeStatus(kind: StatusDocumentKind, raw: string | null | undefined): StatusDescription {
  const value = normalizeStatusValue(raw);
  const entry = STATUS_MAPS[kind][value];
  if (entry) return { value, label: entry.label, tone: entry.tone };
  return { value, label: sentenceCaseStatus(value), tone: 'neutral' };
}

/** Every known status of a document kind, in map order (for filters). */
export function knownStatuses(kind: StatusDocumentKind): string[] {
  return Object.keys(STATUS_MAPS[kind]);
}

/** Statuses a document kind still works on (the libraries' default "Open" filter). */
const OPEN_STATUSES: { readonly [kind in 'transfer' | 'purchaseOrder' | 'subrental']: readonly string[] } = {
  transfer: [
    'draft',
    'confirmed',
    'partially_picked',
    'picked',
    'partially_shipped',
    'shipped',
    'in_transit',
    'partially_received',
  ],
  purchaseOrder: ['draft', 'submitted', 'acknowledged', 'partially_received'],
  subrental: ['draft', 'submitted', 'acknowledged', 'on_rent', 'partially_received', 'return_overdue'],
};

export function openStatuses(kind: 'transfer' | 'purchaseOrder' | 'subrental'): string[] {
  return [...OPEN_STATUSES[kind]];
}

export type StatusTrackerStep = {
  key: string;
  label: string;
  /** Raw statuses that put the document on this step. */
  statuses: readonly string[];
};

const TRANSFER_STEPS: readonly StatusTrackerStep[] = [
  { key: 'draft', label: 'Draft', statuses: ['draft'] },
  { key: 'confirmed', label: 'Confirmed', statuses: ['confirmed'] },
  { key: 'picked', label: 'Picked', statuses: ['partially_picked', 'picked'] },
  { key: 'shipped', label: 'Shipped', statuses: ['partially_shipped', 'shipped'] },
  { key: 'in_transit', label: 'In transit', statuses: ['in_transit'] },
  { key: 'received', label: 'Received', statuses: ['partially_received', 'received'] },
  { key: 'complete', label: 'Complete', statuses: ['complete'] },
];

const PURCHASE_ORDER_STEPS: readonly StatusTrackerStep[] = [
  { key: 'draft', label: 'Draft', statuses: ['draft'] },
  { key: 'submitted', label: 'Submitted', statuses: ['submitted'] },
  { key: 'acknowledged', label: 'Acknowledged', statuses: ['acknowledged'] },
  { key: 'partially_received', label: 'Partially received', statuses: ['partially_received'] },
  { key: 'received', label: 'Received', statuses: ['received'] },
  { key: 'closed', label: 'Closed', statuses: ['closed'] },
];

const SUBRENTAL_STEPS: readonly StatusTrackerStep[] = [
  { key: 'draft', label: 'Draft', statuses: ['draft'] },
  { key: 'submitted', label: 'Submitted', statuses: ['submitted', 'pending'] },
  { key: 'acknowledged', label: 'Acknowledged', statuses: ['acknowledged', 'confirmed'] },
  {
    key: 'on_rent',
    label: 'On rent',
    statuses: ['on_rent', 'active', 'partially_received', 'return_overdue', 'received'],
  },
  { key: 'returned', label: 'Returned', statuses: ['returned'] },
  { key: 'closed', label: 'Closed', statuses: ['closed'] },
];

const TRACKERS: { readonly [kind in 'transfer' | 'purchaseOrder' | 'subrental']: readonly StatusTrackerStep[] } = {
  transfer: TRANSFER_STEPS,
  purchaseOrder: PURCHASE_ORDER_STEPS,
  subrental: SUBRENTAL_STEPS,
};

export function trackerSteps(kind: 'transfer' | 'purchaseOrder' | 'subrental'): readonly StatusTrackerStep[] {
  return TRACKERS[kind];
}

/** The tracker step a raw status sits on, or null (cancelled, unknown). */
export function trackerStepKey(
  kind: 'transfer' | 'purchaseOrder' | 'subrental',
  raw: string | null | undefined,
): string | null {
  const value = normalizeStatusValue(raw);
  if (!value) return null;
  return TRACKERS[kind].find((step) => step.statuses.includes(value))?.key ?? null;
}

/** Exception label for a stopped document, shown beside its tracker. */
export function trackerExceptionLabel(
  kind: StatusDocumentKind,
  raw: string | null | undefined,
): string | null {
  const description = describeStatus(kind, raw);
  return description.tone === 'stopped' ? description.label : null;
}
